import {
  SiteInfoResponseModelV2,
  SitesInfo200,
} from '@newfold/huapi-js/src/index.schemas';

import { SiteInfoType } from '~/types/site';

import {
  AI_PLANS,
  GET_ONLINE_FREE_TRIAL_SKU,
  GET_ONLINE_PAID_SKUS,
  SELL_ONLINE_FREE_TRIAL_SKU,
  SELL_ONLINE_PAID_SKUS,
  STATUSES,
} from './constants';
import { SiteIDType } from './types';

export const canonicalizeUrl = (url: string) => url?.replace(/\/+$/, '');

export const checkDeletingStatus = (status: string | undefined) =>
  STATUSES.DELETING.includes(status!);

export const checkFailedDelete = (status: string | undefined) =>
  STATUSES.FAILED_DELETE.includes(status!);

export const checkResettingStatus = (status: string | undefined) =>
  STATUSES.RESETTING.includes(status!);

export const checkFailedReset = (status: string | undefined) =>
  STATUSES.FAILED_RESET.includes(status!);

export const checkFailedStatus = (status: string | undefined) =>
  STATUSES.FAILED.includes(status!);

export const checkCreatingStatus = (status: string | undefined) =>
  STATUSES.CREATING.includes(status!);

export const checkSuspendedStatus = (status: string | undefined) =>
  STATUSES.SUSPENDED.includes(status!);

export const getSiteStatus = (status: string | undefined) => {
  if (STATUSES.CREATING.includes(status!)) {
    return 'creating';
  }
  if (STATUSES.FAILED.includes(status!)) {
    return 'failed';
  }
};

export const checkWordPressType = ({ type, detected_type }: any) => {
  return type === 'wordpress' && type === detected_type;
};

export const getCanShowLogin = ({ status, type, detected_type }: any) =>
  checkWordPressType({ type, detected_type }) &&
  !checkCreatingStatus(status) &&
  !checkFailedStatus(status) &&
  !checkFailedReset(status) &&
  !checkDeletingStatus(status) &&
  !checkFailedDelete(status) &&
  !checkResettingStatus(status);

export const getCanShowDocrootMismatchAlert = (status?: string) => {
  return status === 'docroot_mismatch';
};

export const getCanShowDocrootMissingAlert = ({ docroot }: any) =>
  docroot === null;

export const getCanShowFixSiteModal = ({ status, type, detected_type }: any) =>
  type === 'wordpress' &&
  type !== detected_type &&
  !checkCreatingStatus(status) &&
  !checkFailedStatus(status);

export const getIsPendingAction = (site: any) =>
  checkFailedStatus(site?.status) ||
  checkCreatingStatus(site?.status) ||
  checkFailedReset(site?.status) ||
  checkResettingStatus(site?.status) ||
  checkDeletingStatus(site?.status) ||
  checkFailedDelete(site?.status);

export const getSiteDisabled = (status: string | undefined | null) => {
  if (!status) return true;
  return STATUSES.SUSPENDED.includes(status?.toLowerCase());
};

export function getSiteUrlWithoutProtocol(url?: string | null) {
  if (typeof url !== 'string') return url;
  return url.replace(/(^https?:\/\/|\/+$)/g, '');
}

/**
 * Returns true if the path matches the pattern for non-Atomic staging sites
 *
 * Valid examples:
 * - public_html/staging/4384
 * - public_html/staging/4384/
 * - public_html/test/staging/4384
 * - public_html/test/test/staging/4384
 * - public_html/test1/test2/test3/staging/12345/
 *
 * Invalid examples:
 * - staging/4384                     (missing public_html prefix)
 * - public_html/staging/abcd         (not a number)
 * - public_html/test/production/4384 (missing "staging" in path)
 */
export const isWpStagingSite = (docpath: string = ''): boolean => {
  const regex = /^public_html(?:\/[^/]+)*\/staging\/\d+\/?$/;
  return regex.test(docpath);
};

/**
 * @description return a list containing the docroot and docpath of a site
 * @param site SiteInfoType
 */
export const getDocrootsFromSite = (site?: SiteInfoType): string[] => {
  return [
    ...(!!site?.docroot ? [site.docroot!] : []),
    ...(!!site?.docpath ? [site.docpath!] : []),
  ];
};

// TODO: update any to be a union of possible site list types when
//       the site id type in v3 site list is updated to be compatible (string | number | undefined).
//       currently v3 site id is only string | undefined.
export const removeDeletedSites = (sites?: any[]) =>
  sites?.filter((site: any) => !site?.status?.includes('deleted'));

export const isSiteIdAvailable = (siteId: SiteIDType) => {
  try {
    return !!siteId && !isNaN(Number(siteId));
  } catch (error) {
    console.warn('Warning: Error checking siteId availability:', error);
    return false;
  }
};

export const removeProtocolFromUrl = (url: string) => {
  if (url && typeof url === 'string') {
    return url.replace(/^https?:\/\//i, ''); // Removes "http://" or "https://" (case-insensitive)
  }
  return url; // Return as-is if it's not a string or doesn't match
};

export const isFreeTrialAiPlan = (prodCode: string | null | undefined) =>
  prodCode === GET_ONLINE_FREE_TRIAL_SKU ||
  prodCode === SELL_ONLINE_FREE_TRIAL_SKU;

export const isPaidAiPlan = (prodCode: string | null | undefined) => {
  if (prodCode) {
    return (
      GET_ONLINE_PAID_SKUS.includes(prodCode) ||
      SELL_ONLINE_PAID_SKUS.includes(prodCode)
    );
  }
  return false;
};

export const isAiPlan = (prodCode: string | null | undefined) =>
  isFreeTrialAiPlan(prodCode) || isPaidAiPlan(prodCode);

export const isAiSite = (siteInfo?: SitesInfo200 | SiteInfoResponseModelV2) =>
  !!(siteInfo?.install_blu_ai || siteInfo?.install_blu_ai_store);

export const isFreeTrialAiSite = (
  siteInfo: SitesInfo200 | SiteInfoResponseModelV2,
) => isAiSite(siteInfo) && siteInfo?.tenweb_upgrade_date === null;

export const isPaidAiSite = (
  siteInfo: SitesInfo200 | SiteInfoResponseModelV2,
) => isAiSite(siteInfo) && siteInfo?.tenweb_upgrade_date !== null;

export const isSellOnlinePlan = (prodCode: string | null | undefined) => {
  if (prodCode) {
    return (
      SELL_ONLINE_PAID_SKUS.includes(prodCode) ||
      prodCode === SELL_ONLINE_FREE_TRIAL_SKU
    );
  }
  return false;
};

const getPlanName = (
  planCode: string | number | null | undefined,
  defaultName: string,
): string => {
  if (planCode === null || planCode === undefined || planCode === '') {
    return '';
  }

  const normalizedPlanCode = String(planCode);
  return normalizedPlanCode === '1' || normalizedPlanCode === 'premium'
    ? defaultName
    : AI_PLANS[normalizedPlanCode as keyof typeof AI_PLANS] ?? defaultName;
};

export const getPlanDisplayName = (site?: SiteInfoType): string => {
  if (!site) return '';

  return (
    getPlanName(site.install_blu_ai_store, AI_PLANS['so_starter']) ||
    getPlanName(site.install_blu_ai, AI_PLANS['go_starter'])
  );
};
